import type { CSSProperties } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { NuqsAdapter } from "nuqs/adapters/react-router/v6";
import { SidebarProvider } from "@musa.tech/design-system";
import { AuthProvider } from "./components/AuthProvider";
import { DemoModeProvider } from "./contexts/DemoModeContext";
import { QueryCacheDebugger } from "./components/QueryCacheDebugger";
import { useSessionTracking } from "./hooks/useSessionTracking";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { PermissionRoute } from "./components/PermissionRoute";
import { getLegacyRolesForRoute } from "./components/layout/navigation";
import { RouteAccessProvider } from "./components/RouteAccessContext";
import { PERMISSIONS } from "./constants/permissions";
import { InternalRoute } from "./components/InternalRoute";
import { JadlogRoute } from "./components/JadlogRoute";
import { SendMailRoute } from "./components/SendMailRoute";
import { BulkUpdateRoute } from "./components/BulkUpdateRoute";
import { Layout } from "./components/Layout";
import { CloudHumansWidget } from "./components/CloudHumansWidget";
import { isEmbedMode } from "./embed/host";
import { createLocalStoragePersister } from "./lib/queryPersister";
import Dashboard from "./pages/Dashboard";
import DadosImpacto from "./pages/DadosImpacto";
import IndicadoresAmbientais from "./pages/IndicadoresAmbientais";
import DadosDeImpactoJadlog from "./pages/DadosDeImpactoJadlog";
import Coletas from "./pages/Coletas";
import UsersKeycloak from "./pages/UsersKeycloak";
import GestaoUsuario from "./pages/GestaoUsuario";
import MeuPerfil from "./pages/MeuPerfil";
import Suporte from "./pages/Suporte";
import NewOnDemandRequest from "./pages/NewOnDemandRequest";
import Configuracoes from "./pages/Configuracoes";
import CSAT from "./pages/CSAT";
import StatusMetrics from "./pages/StatusMetrics";
import NotificationLogs from "./pages/NotificationLogs";
import SendMail from "./pages/SendMail";
import BulkUpdate from "./pages/BulkUpdate";
import LoginRedirect from "./pages/LoginRedirect";
import PerguntasFrequentes from "./pages/PerguntasFrequentes";
import PublicFAQ from "./pages/PublicFAQ";
import CentralDeAjuda from "./pages/CentralDeAjuda";
import CentralDeAjudaCategoria from "./pages/CentralDeAjudaCategoria";
import TermosDeUso from "./pages/TermosDeUso";
import TermosDeUsoAdmin from "./pages/TermosDeUsoAdmin";
import Login from "./pages/Login";
import LoginMusa from "./pages/LoginMusa";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Unauthorized from "./pages/Unauthorized";
import NotFound from "./pages/NotFound";
import Financeiro from "./pages/Financeiro";
import MedicaoDetalhe from "./pages/MedicaoDetalhe";

// Configuração do QueryClient com cache padrão
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 0, // 5 minutos - tempo que os dados ficam no cache antes de serem coletados pelo garbage collector
      staleTime: 0, // 1 minuto padrão
      refetchOnWindowFocus: false, // Não refetch ao focar na janela
      refetchOnReconnect: false, // Não refetch ao reconectar
      refetchOnMount: false, // Não refetch ao montar o componente (usa cache se disponível)
      retry: 1, // Apenas 1 retry em caso de erro
    },
  },
});

// Configuração do persister para localStorage
const persister = createLocalStoragePersister({
  key: "MUSA_PORTAL_CACHE",
  throttleTime: 1000,
});

// QueryClient configurado silenciosamente

const AppContent = () => {
  useSessionTracking();
  return null;
};

// Redirect legado: mantém URLs antigas /coletasobdemanda/* vivas após o rename
// para /coleta-sob-demanda/* (preserva sub-path e query string).
const LegacyColetaSobDemandaRedirect = () => {
  const location = useLocation();
  const rest = location.pathname.replace(/^\/coletasobdemanda/, "");
  return (
    <Navigate to={`/coleta-sob-demanda${rest}${location.search}`} replace />
  );
};

// Redirect legado: /users-keycloak -> /usuarios (GR-668). Preserva query
// string — o fluxo de recuperação de senha usa
// /users-keycloak?editar=self&senha=true&recovery=true (ver ResetPassword.tsx).
const LegacyUsersKeycloakRedirect = () => {
  const location = useLocation();
  return <Navigate to={`/usuarios${location.search}`} replace />;
};

const App = () => (
  <PersistQueryClientProvider
    client={queryClient}
    persistOptions={{
      persister,
      maxAge: 1000 * 60 * 5, // 5 minutos
      buster: "", // Pode usar uma versão se quiser invalidar cache antigo
    }}
  >
    {/* <QueryCacheDebugger /> */}
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <NuqsAdapter>
        <AuthProvider>
          {/* Chrome, como o menu e o header: quem embute é dono do suporte.
              Também evita um crash — IAFilters chama
              $cloudchat.toggleBubbleVisibility() na montagem e o guard de lá
              confere a API, não o botão; dentro do iframe o SDK chega depois
              e a chamada estoura num efeito do React. */}
          {!isEmbedMode() && <CloudHumansWidget />}
          {/* Depende do perfil autenticado para saber quem pode usar o modo demo */}
          <DemoModeProvider>
          <SidebarProvider
            className="block min-h-0"
            style={
              {
                "--sidebar-width": "243px",
                "--sidebar-width-icon": "80px",
              } as CSSProperties
            }
          >
          <AppContent />
          <Routes>
            {/* Públicas */}
            <Route path="/login" element={<Login />} />
            <Route path="/login/musa" element={<LoginMusa />} />
            <Route path="/login-redirect" element={<LoginRedirect />} />
            <Route path="/login-redirect/musa" element={<LoginRedirect />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/unauthorized" element={<Unauthorized />} />
            <Route path="/perguntasfrequentes/live/:slug" element={<PublicFAQ />} />

            {/* Redirects legados */}
            {/* Rotas antigas unificadas em /coletas (GR-417) */}
            <Route
              path="/coletasrecorrentes"
              element={<Navigate to="/coletas" replace />}
            />
            {/* Sob Demanda agora é uma aba de /coletas (GR-417) */}
            <Route
              path="/coleta-sob-demanda"
              element={<Navigate to="/coletas?tab=sobdemanda" replace />}
            />
            {/* Redirect legado: /coletasobdemanda/* -> /coleta-sob-demanda/* */}
            <Route
              path="/coletasobdemanda"
              element={<LegacyColetaSobDemandaRedirect />}
            />
            <Route
              path="/coletasobdemanda/*"
              element={<LegacyColetaSobDemandaRedirect />}
            />
            {/* Redirect legado: /users-keycloak -> /usuarios (GR-668) */}
            <Route
              path="/users-keycloak"
              element={<LegacyUsersKeycloakRedirect />}
            />

            {/*
              Rotas autenticadas: o Layout é uma rota de layout, montado uma
              única vez por sessão. Trocar de página troca só o <Outlet />,
              sem remontar menu, header e a carga inicial do Redux.
            */}
            <Route
              element={
                <ProtectedRoute>
                  <RouteAccessProvider>
                    <Layout />
                  </RouteAccessProvider>
                </ProtectedRoute>
              }
            >
              <Route
                path="/"
                element={
                  <PermissionRoute anyOf={[PERMISSIONS.VIEW_DASHBOARD]} area="ao Painel">
                    <Dashboard />
                  </PermissionRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <PermissionRoute anyOf={[PERMISSIONS.VIEW_DASHBOARD]} area="ao Painel">
                    <Dashboard />
                  </PermissionRoute>
                }
              />
              <Route
                path="/dadosimpacto"
                element={
                  <PermissionRoute anyOf={[PERMISSIONS.VIEW_ENVIRONMENTAL_INDICATORS]} area="aos Dados de Impacto">
                    <DadosImpacto />
                  </PermissionRoute>
                }
              />
              <Route
                path="/indicadores-ambientais"
                element={
                  <PermissionRoute anyOf={[PERMISSIONS.VIEW_ENVIRONMENTAL_INDICATORS]} area="aos Indicadores Ambientais">
                    <IndicadoresAmbientais />
                  </PermissionRoute>
                }
              />
              <Route
                path="/coletas"
                element={
                  <PermissionRoute anyOf={[PERMISSIONS.VIEW_UPCOMING_COLLECTS, PERMISSIONS.VIEW_COLLECTS_HISTORY, PERMISSIONS.VIEW_ON_DEMAND_COLLECTS]} area="às Coletas">
                    <Coletas />
                  </PermissionRoute>
                }
              />
              <Route
                path="/coleta-sob-demanda/nova"
                element={
                  <PermissionRoute anyOf={[PERMISSIONS.REQUEST_ON_DEMAND_COLLECT_SCHEDULED, PERMISSIONS.REQUEST_ON_DEMAND_COLLECT_EXTRA]} area="à solicitação de coleta">
                    <NewOnDemandRequest />
                  </PermissionRoute>
                }
              />
              {/* Detalhe da solicitação do fluxo antigo (Supabase) saiu com o corte
                  MC-314: link antigo volta para a listagem. */}
              <Route
                path="/coleta-sob-demanda/:id/coleta/:hubCollectId"
                element={<Navigate to="/coletas?tab=sobdemanda" replace />}
              />
              <Route
                path="/coleta-sob-demanda/:id"
                element={<Navigate to="/coletas?tab=sobdemanda" replace />}
              />
              <Route
                path="/financeiro"
                element={
                  <PermissionRoute
                    anyOf={[PERMISSIONS.VIEW_INVOICES]}
                    area="ao Financeiro"
                    legacyRoles={getLegacyRolesForRoute("/financeiro")}
                  >
                    <Financeiro />
                  </PermissionRoute>
                }
              />
              {/* Detalhamento da Medição — página própria, no lugar do drawer (protótipo da PD) */}
              <Route
                path="/financeiro/:id"
                element={
                  <PermissionRoute anyOf={[PERMISSIONS.VIEW_INVOICES]} area="ao Financeiro">
                    <MedicaoDetalhe />
                  </PermissionRoute>
                }
              />
              <Route path="/usuarios" element={<UsersKeycloak />} />
              <Route path="/gestao-usuario" element={<GestaoUsuario />} />
              <Route
                path="/meu-perfil"
                element={
                  <PermissionRoute anyOf={[PERMISSIONS.EDIT_OWN_PROFILE]} area="ao seu perfil">
                    <MeuPerfil />
                  </PermissionRoute>
                }
              />
              <Route path="/configuracoes" element={<Configuracoes />} />
              <Route
                path="/suporte"
                element={
                  <PermissionRoute anyOf={[PERMISSIONS.VIEW_SUPPORT]} area="ao Suporte">
                    <Suporte />
                  </PermissionRoute>
                }
              />
              <Route path="/csat" element={<CSAT />} />
              <Route
                path="/perguntasfrequentes"
                element={
                  <PermissionRoute anyOf={[PERMISSIONS.VIEW_HELP_CENTER]} area="às Perguntas Frequentes">
                    <PerguntasFrequentes />
                  </PermissionRoute>
                }
              />
              <Route
                path="/perguntasfrequentes/:slug"
                element={
                  <PermissionRoute anyOf={[PERMISSIONS.VIEW_HELP_CENTER]} area="às Perguntas Frequentes">
                    <PerguntasFrequentes />
                  </PermissionRoute>
                }
              />
              <Route
                path="/ajuda"
                element={
                  <PermissionRoute anyOf={[PERMISSIONS.VIEW_HELP_CENTER]} area="à Central de Ajuda">
                    <CentralDeAjuda />
                  </PermissionRoute>
                }
              />
              <Route
                path="/ajuda/:categoria"
                element={
                  <PermissionRoute anyOf={[PERMISSIONS.VIEW_HELP_CENTER]} area="à Central de Ajuda">
                    <CentralDeAjudaCategoria />
                  </PermissionRoute>
                }
              />
              <Route
                path="/termosdeuso"
                element={
                  <PermissionRoute anyOf={[PERMISSIONS.VIEW_TERMS]} area="aos Termos de Uso">
                    <TermosDeUso />
                  </PermissionRoute>
                }
              />
              {/* Mitigação temporária: rota Notificações indisponível até correção definitiva */}
              {/* <Route path="/notificacoes" element={<Notificacoes />} /> */}
            </Route>

            <Route
              element={
                <JadlogRoute>
                  <Layout />
                </JadlogRoute>
              }
            >
              <Route
                path="/dadosdeimpactojadlog"
                element={<DadosDeImpactoJadlog />}
              />
            </Route>

            <Route
              element={
                <InternalRoute>
                  <Layout />
                </InternalRoute>
              }
            >
              <Route
                path="/termosdeusomusa"
                element={<TermosDeUsoAdmin />}
              />
            </Route>

            <Route
              element={
                <SendMailRoute>
                  <Layout />
                </SendMailRoute>
              }
            >
              <Route path="/send-mail" element={<SendMail />} />
            </Route>

            <Route
              element={
                <BulkUpdateRoute>
                  <Layout />
                </BulkUpdateRoute>
              }
            >
              <Route path="/bulk-update" element={<BulkUpdate />} />
            </Route>


            {/* Internas, sem o shell do portal */}
            <Route
              path="/status-metrics"
              element={
                <InternalRoute>
                  <StatusMetrics />
                </InternalRoute>
              }
            />
            <Route
              path="/notification-logs"
              element={
                <InternalRoute>
                  <NotificationLogs />
                </InternalRoute>
              }
            />

            <Route path="*" element={<NotFound />} />
          </Routes>
          </SidebarProvider>
          </DemoModeProvider>
        </AuthProvider>
        </NuqsAdapter>
      </BrowserRouter>
    </TooltipProvider>
  </PersistQueryClientProvider>
);

//force deploy

export default App;
// force deploy
